const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');

const DocsPage = component(() => ({
  view: () => m('section.panel.docs-page', [
    m(Breadcrumbs, { items: [{ label: 'Docs' }] }),
    m('h2', 'Spache docs'),
    m('p', 'Guides for connecting Spache to your site, deployment workflow, and content systems.'),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/features' }, 'Spache features')),
      m('p', 'Browse the current capabilities for rendering, API replay, cache control, domains, analytics, and billing.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/mcp' }, 'Use Spache with an AI agent')),
      m('p', 'Connect Spache’s remote MCP server and manage sites, inspect usage, and update settings through your AI client.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/pricing' }, 'Pricing')),
      m('p', 'See what is included in the free plan, when a paid plan is needed, and how usage overages are calculated.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/how-it-works' }, 'How Spache works')),
      m('p', 'Follow Spache.online from its JavaScript app shell through browser rendering to cached HTML and faster page startup.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/put-your-site-in-spache' }, 'How to put your site in Spache')),
      m('p', 'Create a site, test it on a preview domain, then connect your public domain when you are ready.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/cache-invalidation' }, 'Cache invalidation webhooks')),
      m('p', 'Invalidate cached pages after GitHub Actions deployments, Render deploys, or individual CMS content updates.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/script-loading' }, 'Load your JavaScript without blocking HTML')),
      m('p', 'When to use defer, why async is usually a poor fit for an app bundle, and what visitors experience before the app mounts.')
    ]),
    m('section.docs-page__article', [
      m('h3', m('a', { href: '/docs/comparisons' }, 'Comparisons')),
      m('p', 'See how different rendering approaches would handle a real custom storefront, Mid Engine Co.')
    ])
  ])
}));

const CacheInvalidationDocsPage = component(() => ({
  view: () => m('section.panel.docs-page', [
    m(Breadcrumbs, {
      items: [
        { label: 'Docs', href: '/docs' },
        { label: 'Cache invalidation webhooks' }
      ]
    }),
    m('h2', 'Cache invalidation webhooks'),
    m('p', 'Tell Spache when your origin has changed so visitors do not wait for the normal cache lifetime or dependency check.'),
    m('h3', 'Generate a webhook URL'),
    m('ol', [
      m('li', 'Open a site and go to Settings → Webhooks.'),
      m('li', 'Click Generate webhook URL.'),
      m('li', 'Copy the URL into the secret store used by your deployment system.'),
      m('li', 'If the URL is exposed, revoke it and generate a replacement.')
    ]),
    m('h3', 'Invalidate the whole site'),
    m('p', 'Use this after a successful deployment. It invalidates all cached routes for the site.'),
    m('pre', m('code', `curl --fail --retry 3 \\
  -X POST "$SPACHE_WEBHOOK_URL" \\
  -H 'Content-Type: application/json' \\
  --data '{"mode":"site","source":"deployment"}'`)),
    m('h3', 'Invalidate one route'),
    m('p', 'Use this when a CMS or content update affects only one route.'),
    m('pre', m('code', `curl --fail --retry 3 \\
  -X POST "$SPACHE_WEBHOOK_URL" \\
  -H 'Content-Type: application/json' \\
  --data '{"mode":"route","path":"/products/example","source":"cms"}'`)),
    m('h3', 'GitHub Actions'),
    m('p', 'Add the generated URL as a repository or environment secret named SPACHE_WEBHOOK_URL. Call it after the deployment step succeeds.'),
    m('pre', m('code', `- name: Invalidate Spache cache
  if: success()
  env:
    SPACHE_WEBHOOK_URL: \${{ secrets.SPACHE_WEBHOOK_URL }}
  run: |
    curl --fail --retry 3 \\
      -X POST "$SPACHE_WEBHOOK_URL" \\
      -H 'Content-Type: application/json' \\
      --data '{"mode":"site","source":"github","commit":"'"$GITHUB_SHA"'"}'`)),
    m('h3', 'Render'),
    m('p', 'Create a Render workspace webhook and point it at the generated Spache URL. Subscribe to deploy_ended events and configure the webhook to send only successful deployments, if that option is available in your Render plan. If your Render webhook also sends failed or cancelled deployments, do not invalidate for those events.'),
    m('p', 'The generated Spache URL is the authentication credential for this endpoint. Keep it in Render’s secret configuration where possible, and do not paste it into site content or client-side code.'),
    m('h3', 'Delivery behavior'),
    m('p', 'Webhook delivery is safe to retry. Site invalidation bumps the site cache version; route invalidation creates a new version for that route. The response confirms that the invalidation was accepted, not that a new render has completed.')
  ])
}));

const ScriptLoadingDocsPage = component(() => ({
  view: () => m('article.panel.docs-page', [
    m(Breadcrumbs, {
      items: [
        { label: 'Docs', href: '/docs' },
        { label: 'Load your JavaScript without blocking HTML' }
      ]
    }),
    m('h2', 'Load your JavaScript without blocking HTML'),
    m('p',
      'For a classic external app bundle, defer is usually the right default. It lets the browser download ' +
      'JavaScript while parsing the document, then runs it after parsing is complete.'
    ),
    m('pre', m('code', '<script src="/index.js" defer></script>')),
    m('h3', 'Why defer instead of async?'),
    m('ul', [
      m('li', 'Deferred scripts run after the document has been parsed.'),
      m('li', 'Multiple deferred scripts execute in document order.'),
      m('li', 'They still run before DOMContentLoaded, which suits apps that mount after the document is ready.'),
      m('li', 'Async scripts run as soon as each download finishes, so execution order is unpredictable.')
    ]),
    m('p',
      'Defer does not make JavaScript execution non-blocking. When the browser starts executing the bundle, ' +
      'that work still occupies the main thread until it yields.'
    ),
    m('h3', 'What a visitor may see'),
    m('p',
      'This is where Spache helps: on a cache hit, the server can send useful rendered page content immediately, ' +
      'instead of making the browser wait for the app bundle to run before it can build the page. With defer, the ' +
      'browser can parse that HTML and discover its resources while downloading the bundle. That gives the page a ' +
      'chance to show meaningful content sooner, and makes that content available in the HTML that crawlers receive.'
    ),
    m('p',
      'The browser may paint the rendered content before the deferred bundle runs, but it is not guaranteed to paint ' +
      'first: a fast download or other browser work can put script execution before the first visible paint.'
    ),
    m('p',
      'When the app mounts, it may replace or reconcile the rendered page. Until that work finishes, the content can ' +
      'be visible but not interactive. That is still useful: visitors can start reading real content before the app is ' +
      'ready, rather than staring at an empty page. If mounting replaces the whole body, users may see the rendered ' +
      'content change rather than get an interactive pause before takeover.'
    ),
    m('p',
      'Spache can also capture eligible API responses while rendering and inject them into the cached page for the app ' +
      'to replay during startup. When those calls match, the app can get its data without waiting for another round trip ' +
      'to your origin. This often makes the gap between seeing the rendered page and using the app very small, sometimes ' +
      'effectively zero; JavaScript still has to execute, and uncaptured requests still use the network.'
    ),
    m('h3', 'Practical recommendations'),
    m('ul', [
      m('li', 'Use defer for classic app bundles; avoid async when startup scripts depend on one another or on parsed markup.'),
      m('li', 'Keep the rendered HTML useful on its own, especially the main content and important links.'),
      m('li', 'Avoid clearing the whole body at startup. Mount into a dedicated app root and preserve useful rendered content until the app is ready where possible.'),
      m('li', 'For genuinely interactive content before JavaScript takes over, use progressive enhancement or hydration. Defer alone does not provide that.')
    ]),
    m('p',
      'Spache makes rendered content available before the visitor’s app bundle executes; it does not turn a client-side ' +
      'app into a hydration-based application. Test with a throttled network and CPU in browser developer tools to see ' +
      'whether your real page paints, changes, and becomes interactive in the order you expect.'
    ),
    m('a', { href: '/docs/how-spache-works' }, 'How Spache works →')
  ])
}));

const PutYourSiteInSpacheDocsPage = component(() => ({
  view: () => m('article.panel.docs-page', [
    m(Breadcrumbs, {
      items: [
        { label: 'Docs', href: '/docs' },
        { label: 'How to put your site in Spache' }
      ]
    }),
    m('h2', 'How to put your site in Spache'),
    m('p',
      'Start with a preview. You can check that Spache can render your site before changing where your public domain points.'
    ),
    m('h3', '1. Add your site'),
    m('ol', [
      m('li', 'Open Sites and choose Add site.'),
      m('li', 'Enter a name and the full URL where your app is currently hosted, including https://.'),
      m('li', 'Save the site. Keep your origin online; Spache needs to fetch it to render and refresh pages.')
    ]),
    m('h3', '2. Open the preview'),
    m('p',
      'After saving, Spache gives the site a preview URL. Open it from your Sites list. The first visit may need to fetch and render the page; later visits can use the cached HTML.'
    ),
    m('ul', [
      m('li', 'Check the homepage and a few important deep links.'),
      m('li', 'Try the interactions that matter, including pages that load data from your APIs.'),
      m('li', 'Review the site Overview, Routes, and route details for render results or errors.')
    ]),
    m('p', 'Your existing origin and its DNS are unchanged while you use the preview URL.'),
    m('h3', '3. Add your public domain'),
    m('p',
      'When the preview looks right, open the site’s Settings → Domains. Add the hostname visitors should use, such as www.example.com. Enter only the hostname—no protocol, path, or trailing slash.'
    ),
    m('p',
      'Connecting a public domain may require an active paid plan. The app will show the account requirement before provisioning.'
    ),
    m('h3', '4. Verify DNS and wait for HTTPS'),
    m('ol', [
      m('li', 'Copy the DNS verification record Spache provides into your DNS provider. It is usually an _acme-challenge CNAME.'),
      m('li', 'Leave the existing traffic record in place for now. Wait for Spache to confirm the verification record and issue the HTTPS certificate.'),
      m('li', 'Use Refresh certificate status in Domains if you have added the record but the status has not updated yet.')
    ]),
    m('h3', '5. Point traffic to Spache'),
    m('p',
      'Only after the certificate is active, replace the hostname’s existing A record with the Spache edge IP shown in Domains. Keep the _acme-challenge CNAME in place.'
    ),
    m('p',
      'Visit the hostname over HTTPS, then use Test domain in the Domains section. The site Overview also reports whether Spache has received traffic for the connected domain.'
    ),
    m('h3', 'What happens after cutover?'),
    m('p',
      'Visitors keep using your domain. Spache receives the request, serves a prepared page when it has a valid cached render, and fetches from your origin when needed. Your app bundle still runs in the visitor’s browser; Spache prepares and serves the rendered HTML so useful content can arrive earlier and be read by crawlers.'
    ),
    m('p', [
      'Learn more about ',
      m('a', { href: '/docs/how-spache-works' }, 'how Spache works'),
      ' or configure ',
      m('a', { href: '/docs/cache-invalidation' }, 'cache invalidation after deployments'),
      '.'
    ])
  ])
}));

module.exports = {
  CacheInvalidationDocsPage,
  DocsPage,
  PutYourSiteInSpacheDocsPage,
  ScriptLoadingDocsPage
};
